/**
 * fetchWithRetry — wrapper around fetch() that retries on transient errors.
 *
 * Retries on:
 *  - Network errors (TypeError thrown by fetch when offline / DNS hiccup / CORS blip)
 *  - 502 Bad Gateway, 503 Service Unavailable, 504 Gateway Timeout
 *  - 429 Too Many Requests
 *
 * Does NOT retry on 4xx (other than 429) — those are real auth/permission/validation errors
 * and retrying would just hammer the backend.
 *
 * Default policy: 3 attempts total (1 initial + 2 retries), with 350ms / 900ms backoff.
 * Total worst-case extra latency ≈ 1.3s before surfacing the failure to the caller.
 *
 * Use this for read-only GETs that the UI displays. Avoid wrapping POST/PUT/DELETE without
 * idempotency keys because retrying a write could double-create a resource.
 */
const TRANSIENT_STATUSES = new Set([429, 502, 503, 504]);

export async function fetchWithRetry(url, init = {}, { retries = 2, backoffs = [350, 900] } = {}) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const res = await fetch(url, init);
      if (res.ok) return res;
      if (!TRANSIENT_STATUSES.has(res.status) || attempt === retries) return res;
      lastError = new Error(`Transient HTTP ${res.status}`);
    } catch (err) {
      // Network failure (fetch throws TypeError). Retry unless this is the final attempt.
      lastError = err;
      if (attempt === retries) throw err;
    }
    await new Promise((r) => setTimeout(r, backoffs[Math.min(attempt, backoffs.length - 1)] || 1000));
  }
  // Shouldn't be reachable, but keeps the contract explicit.
  throw lastError;
}
